import {useQuery} from '@tanstack/react-query';
import {useConfig} from '@lore/config';

/**
 * @typedef {Object} HealthUser
 * @property {number} id
 * @property {string} firstName
 * @property {string} lastName
 * @property {string} updatedAt
 */

/**
 * @typedef {Object} HealthSite
 * @property {number} id
 * @property {string} name
 * @property {string} city
 * @property {string} state
 * @property {number|null} networkId
 * @property {string|null} networkName
 * @property {boolean|null} networkIsActive
 * @property {number} networkCount
 * @property {number} distance_meters
 */

/**
 * @typedef {Object} HealthNetwork
 * @property {string} id
 * @property {string} name
 */

/**
 * @typedef {Object} HealthSection
 * @property {number} total
 * @property {HealthUser[]|HealthSite[]|HealthNetwork[]} data
 */

/**
 * @typedef {Object} SiteHealth
 * @property {number} siteId
 * @property {number} limit
 * @property {HealthSection} educators
 * @property {HealthSection} partners
 * @property {HealthSection} sites
 * @property {HealthSection} sites_single
 * @property {HealthSection} networks
 */

/**
 * dummy export to allow JSDoc types to be imported by other files
 * @type {HealthUser}
 */
export const _HealthUser = {};

/**
 * dummy export to allow JSDoc types to be imported by other files
 * @type {HealthNetwork}
 */
export const _HealthNetwork = {};

/**
 * dummy export to allow JSDoc types to be imported by other files
 * @type {HealthSite}
 */
export const _HealthSite = {};

/**
 * dummy export to allow JSDoc types to be imported by other files
 * @type {HealthSection}
 */
export const _HealthSection = {};

/**
 * dummy export to allow JSDoc types to be imported by other files
 * @type {SiteHealth}
 */
export const _SiteHealth = {};

const STALE_TIME = 15 * 60 * 1000; // 15 minutes

/**
 * Custom hook to fetch site health from the core API.
 *
 * @param {number|string} siteId - The ID of the site to fetch health for.
 * @param {Object} [params] - Optional parameters.
 * @param {number} [params.limit=20] - Number of items to return in each section.
 * @param {Object} [options] - Configuration options like 'enabled' to control the query.
 * @param {boolean} [options.enabled=true] - Whether the query is enabled.
 * @returns {import('@tanstack/react-query').UseQueryResult<SiteHealth>} React Query result object.
 */
export default function useCoreSiteHealth(siteId, {limit = 20} = {}, {enabled = true} = {}) {
  const config = useConfig();
  const {apiRoot, headers} = config.connections.default;

  const query = useQuery({
    queryKey: ['sites', siteId, 'health', {limit}],

    queryFn: () => {
      const url = new URL(`${apiRoot}/core/sites/${siteId}/health`);
      url.searchParams.append('limit', limit);

      return fetch(url.toString(), {
        method: 'GET',
        headers: headers(),
      }).then((res) => {
        if (!res.ok) {
          throw new Error(`Failed to fetch health for site ${siteId}`);
        }
        return res.json();
      });
    },

    select: (data) => {
      let networks = []
      let singleSites = [];

      // add the networks and sites_single categories
      if (data.sites && data.sites.data) {
        singleSites = data.sites.data.filter((site) => site.networkCount === 1);
        const networkMap = new Map(data.sites.data.map((site) => [site.networkId, {id: site.networkId, name: site.networkName}]));
        networks = Array.from(networkMap.values().filter((network) => network.id !== null));
      }

      return {
        ...data,
        single_site: {
          total: singleSites.length,
          data: singleSites,
        },
        network: {
          total: networks.length,
          data: networks,
        },
      };
    },

    staleTime: STALE_TIME,
    enabled: siteId != null && !isNaN(siteId) && enabled,
  });

  return {
    ...query,
  };
}
